import type { CameraStatus, Severity } from '@playplate/shared';
import clsx from 'clsx';
import { AlertTriangle, Inbox, Loader2, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { CAMERA_STATUS_STYLES, SEVERITY_STYLES } from '../lib/format';

/* -------------------------------------------------------------------------- */
/* State components                                                           */
/*                                                                            */
/* Every data surface in this app renders one of loading / error / empty /     */
/* content. Sharing these keeps that discipline from eroding screen by screen. */
/* -------------------------------------------------------------------------- */

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-muted" role="status" aria-live="polite">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="panel flex flex-col items-center gap-3 px-6 py-10 text-center" role="alert">
      <AlertTriangle className="h-6 w-6 text-high" aria-hidden />
      <div>
        <p className="font-display text-base">{title}</p>
        {message ? <p className="mt-1 max-w-md text-sm text-muted">{message}</p> : null}
      </div>
      {onRetry ? (
        <button type="button" className="btn-secondary mt-1" onClick={onRetry}>
          <RefreshCw className="h-4 w-4" aria-hidden />
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message?: string;
  action?: ReactNode;
}) {
  return (
    <div className="panel flex flex-col items-center gap-3 px-6 py-12 text-center">
      <Inbox className="h-6 w-6 text-muted" aria-hidden />
      <div>
        <p className="font-display text-base">{title}</p>
        {message ? <p className="mt-1 max-w-md text-sm text-muted">{message}</p> : null}
      </div>
      {action}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Display components                                                         */
/* -------------------------------------------------------------------------- */

export function StatCard({
  label,
  value,
  hint,
  tone = 'default',
  pending,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'default' | 'good' | 'warn' | 'bad';
  /** True while there is not enough history for the number to mean anything. */
  pending?: boolean;
}) {
  const toneClass = {
    default: 'text-paper',
    good: 'text-online',
    warn: 'text-high',
    bad: 'text-critical',
  }[tone];

  return (
    <div className="panel px-4 py-4">
      <p className="label">{label}</p>
      <p className={clsx('mt-2 font-display text-3xl leading-none', pending ? 'text-muted/50' : toneClass)}>
        {pending ? '—' : value}
      </p>
      {/* An honest placeholder beats a confident zero: on a security dashboard a
          fabricated number is worse than an admitted gap. */}
      <p className="mt-2 text-xs text-muted">{pending ? 'Collecting data' : hint}</p>
    </div>
  );
}

export function SeverityBadge({ severity, count }: { severity: Severity; count?: number }) {
  const style = SEVERITY_STYLES[severity];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        style.text,
        style.bg,
        style.border,
      )}
    >
      <span className={clsx('h-1.5 w-1.5 rounded-full', style.dot)} aria-hidden />
      {severity}
      {count !== undefined && count > 1 ? <span className="opacity-70">×{count}</span> : null}
    </span>
  );
}

export function CameraStatusPill({ status, fps }: { status: CameraStatus; fps?: number | null }) {
  const style = CAMERA_STATUS_STYLES[status];
  return (
    <span className={clsx('inline-flex items-center gap-1.5 text-xs font-semibold', style.text)}>
      <span
        className={clsx('h-2 w-2 rounded-full', style.dot, status === 'ONLINE' && 'animate-pulse-slow')}
        aria-hidden
      />
      {style.label}
      {fps !== undefined && fps !== null && status !== 'OFFLINE' && status !== 'DISABLED' ? (
        <span className="font-mono text-[11px] font-normal text-muted">{fps.toFixed(1)} fps</span>
      ) : null}
    </span>
  );
}

export function ConnectionBadge({ status }: { status: 'connecting' | 'open' | 'reconnecting' | 'offline' }) {
  const config = {
    open: { label: 'Live', dot: 'bg-online', text: 'text-online' },
    connecting: { label: 'Connecting', dot: 'bg-muted', text: 'text-muted' },
    reconnecting: { label: 'Reconnecting', dot: 'bg-high', text: 'text-high' },
    offline: { label: 'Offline', dot: 'bg-critical', text: 'text-critical' },
  }[status];

  return (
    <span
      className={clsx('inline-flex items-center gap-1.5 text-xs font-semibold', config.text)}
      title={
        status === 'open'
          ? 'Receiving live updates'
          : 'Live updates interrupted — data will refresh automatically when the connection returns'
      }
    >
      <span className={clsx('h-2 w-2 rounded-full', config.dot)} aria-hidden />
      {config.label}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl tracking-tight">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx('panel overflow-hidden', className)}>
      {title ? (
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="label">{title}</h2>
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}
